import React, { useEffect, useState, useCallback } from 'react';
import { Snackbar, Alert, Stack } from '@mui/material';
import { subscribeToast, ToastRecord } from '../../lib/toast';

/**
 * Renders the global toast stack. Mount once near the app root.
 * Subscribes to the module-level toast bus so any code (React or not) can emit.
 */
const ToastHost: React.FC = () => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);

  useEffect(() => {
    return subscribeToast((t) => {
      setToasts((prev) => [...prev, t]);
    });
  }, []);

  const handleClose = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: (theme) => theme.zIndex.snackbar + 1,
      }}
    >
      {toasts.map((t) => (
        <Snackbar
          key={t.id}
          open
          // Stack manually (position handled by parent), so disable MUI anchoring offset
          sx={{ position: 'static', transform: 'none' }}
          autoHideDuration={t.duration ?? undefined}
          onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            handleClose(t.id);
          }}
        >
          <Alert
            severity={t.severity}
            variant="filled"
            onClose={() => handleClose(t.id)}
            sx={{ minWidth: 280, boxShadow: 3 }}
          >
            {t.message}
          </Alert>
        </Snackbar>
      ))}
    </Stack>
  );
};

export default ToastHost;
